import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  AlertTriangle,
  ArrowUp,
  Bot,
  Brain,
  Calendar,
  Clock,
  Cloud,
  HardDrive,
  Info,
  Key,
  Loader2,
  MessageCircle,
  Mic,
  PlayCircle,
  Plus,
  Power,
  Save,
  Server,
  Sliders,
  Trash2,
  Volume2,
  Waves,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useCambiosSinGuardar } from '../../hooks/useCambiosSinGuardar';
import BadgeCambiosSinGuardar from '../../components/ui/BadgeCambiosSinGuardar';
import styles from './ConfigIAPage.module.css';

/* ─── Cloud Functions ─────────────────────────────────────── */

// Callables genéricos de secretos (whitelist: GEMINI_API_KEY, LIVEKIT_API_KEY, LIVEKIT_API_SECRET)
const obtenerEstadoSecretoFn = httpsCallable<
  { nombre: string },
  { configured: boolean; masked: string }
>(functions, 'portal_obtenerEstadoSecreto');

const actualizarSecretoFn = httpsCallable<
  { nombre: string; valor: string },
  { success: boolean; masked: string }
>(functions, 'portal_actualizarSecreto');

/* ─── Defaults (idénticos al contrato / valores hardcodeados) ─ */

const DEFAULT_MODELS = [
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'gemini-1.5-flash',
];

interface LlmParam {
  temperature: number;
  maxOutputTokens: number;
}

type LlmKey =
  | 'triage'
  | 'soap'
  | 'sentimiento'
  | 'asistenteMedico'
  | 'resumenPaciente'
  | 'asistenteAdmin';

const LLM_KEYS: { key: LlmKey; label: string }[] = [
  { key: 'triage', label: 'Triage' },
  { key: 'soap', label: 'Notas SOAP' },
  { key: 'sentimiento', label: 'Análisis de sentimiento' },
  { key: 'asistenteMedico', label: 'Asistente médico' },
  { key: 'resumenPaciente', label: 'Resumen para paciente' },
  { key: 'asistenteAdmin', label: 'Asistente de configuración' },
];

const DEFAULT_LLM_PARAMS: Record<LlmKey, LlmParam> = {
  triage: { temperature: 0.4, maxOutputTokens: 2048 },
  soap: { temperature: 0.3, maxOutputTokens: 4096 },
  sentimiento: { temperature: 0.2, maxOutputTokens: 4096 },
  asistenteMedico: { temperature: 0.3, maxOutputTokens: 4096 },
  resumenPaciente: { temperature: 0.5, maxOutputTokens: 2048 },
  asistenteAdmin: { temperature: 0.2, maxOutputTokens: 2048 },
};

type VideoProveedor = 'cloud' | 'selfhosted';

interface LivekitConfig {
  proveedor: VideoProveedor;
  url: string;
  urlSelfHosted: string;
  tokenTtlMedico: string;
  maxParticipantes: number;
  emptyTimeoutSeg: number;
  departureTimeoutSeg: number;
  maxDuracionSeg: number;
  rateLimitVentanaMin: number;
  rateLimitMax: number;
}

const DEFAULT_LIVEKIT: LivekitConfig = {
  proveedor: 'cloud',
  url: '',
  urlSelfHosted: '',
  tokenTtlMedico: '2h',
  maxParticipantes: 10,
  emptyTimeoutSeg: 300,
  departureTimeoutSeg: 20,
  maxDuracionSeg: 7200,
  rateLimitVentanaMin: 30,
  rateLimitMax: 30,
};

/* ─── Proveedores multi-stack (ADDENDUM 3) ─────────────────── */

interface ProveedorOption {
  value: string;
  label: string;
  env?: string; // variable de entorno de la API key (no-google)
  modeloHint?: string; // hint/placeholder del campo modelo
}

const STT_PROVEEDORES: ProveedorOption[] = [
  { value: 'google', label: 'Google', modeloHint: 'default' },
  { value: 'deepgram', label: 'Deepgram Nova-3', env: 'DEEPGRAM_API_KEY', modeloHint: 'nova-3' },
  { value: 'assemblyai', label: 'AssemblyAI', env: 'ASSEMBLYAI_API_KEY', modeloHint: 'best' },
  {
    value: 'speechmatics',
    label: 'Speechmatics',
    env: 'SPEECHMATICS_API_KEY',
    modeloHint: 'enhanced',
  },
  { value: 'cartesia', label: 'Cartesia Ink', env: 'CARTESIA_API_KEY', modeloHint: 'ink-whisper' },
];

const TTS_PROVEEDORES: ProveedorOption[] = [
  { value: 'google', label: 'Google', modeloHint: 'default' },
  { value: 'cartesia', label: 'Cartesia Sonic-3', env: 'CARTESIA_API_KEY', modeloHint: 'sonic-3' },
  { value: 'deepgram', label: 'Deepgram Aura-2', env: 'DEEPGRAM_API_KEY', modeloHint: 'aura-2' },
  { value: 'elevenlabs', label: 'ElevenLabs', env: 'ELEVENLABS_API_KEY', modeloHint: 'eleven_multilingual_v2' },
  { value: 'rime', label: 'Rime', env: 'RIME_API_KEY', modeloHint: 'mistv2' },
];

const LLM_PROVEEDORES: ProveedorOption[] = [
  { value: 'google', label: 'Gemini', modeloHint: 'gemini-2.5-flash' },
  { value: 'openai', label: 'OpenAI GPT-4o-mini', env: 'OPENAI_API_KEY', modeloHint: 'gpt-4o-mini' },
  {
    value: 'anthropic',
    label: 'Anthropic Claude Haiku 4.5',
    env: 'ANTHROPIC_API_KEY',
    modeloHint: 'claude-haiku-4-5',
  },
];

const TURN_DETECTION_OPTIONS: { value: string; label: string }[] = [
  { value: 'vad', label: 'Solo VAD (Silero)' },
  { value: 'multilingual', label: 'Semántica multilingüe (LiveKit Turn Detector)' },
];

/** Opciones del proveedor de respaldo: los mismos de la lista más "ninguno". */
function opcionesRespaldo(proveedores: ProveedorOption[]): ProveedorOption[] {
  return [...proveedores, { value: 'ninguno', label: 'Ninguno (sin respaldo)' }];
}

function modeloHintFor(proveedores: ProveedorOption[], value: string): string {
  return proveedores.find((p) => p.value === value)?.modeloHint ?? '';
}

function envFor(proveedores: ProveedorOption[], value: string): string | undefined {
  return proveedores.find((p) => p.value === value)?.env;
}

/* ─── Nota general (compartida por STT / TTS / Agente de voz) ─ */

const NOTA_PROVEEDORES =
  'Las claves API de estos proveedores se configuran como variables de entorno en el ' +
  'despliegue de los agentes (deploy.sh); aquí seleccionas el proveedor y sus parámetros. ' +
  'La selección aplica en la próxima sesión del agente.';

interface SttConfig {
  proveedor: string;
  idioma: string;
  modelo: string;
  // Respaldo automático: si el primario falla (sin saldo, llave rechazada,
  // caído), los agentes pasan a este sin cortar. 'ninguno' lo desactiva.
  proveedorRespaldo: string;
  respaldoModelo: string;
  respaldoIdioma: string;
  sampleRate: number;
  minConfidence: number;
  interimResults: boolean;
  punctuate: boolean;
}

const DEFAULT_STT: SttConfig = {
  proveedor: 'deepgram',
  idioma: 'multi',
  modelo: 'nova-3',
  proveedorRespaldo: 'google',
  respaldoModelo: 'default',
  respaldoIdioma: 'es-US',
  sampleRate: 48000,
  minConfidence: 0.0,
  interimResults: true,
  punctuate: true,
};

interface TtsConfig {
  proveedor: string;
  idioma: string;
  modelo: string;
  voz: string;
  useStreaming: boolean;
}

const DEFAULT_TTS: TtsConfig = {
  proveedor: 'google',
  idioma: 'es-US',
  modelo: '',
  voz: 'es-US-Neural2-A',
  useStreaming: false,
};

interface AgenteVozConfig {
  llmProveedor: string;
  llmModel: string;
  turnDetection: string;
  ttsVoice: string;
}

const DEFAULT_AGENTE_VOZ: AgenteVozConfig = {
  llmProveedor: 'google',
  llmModel: 'gemini-2.5-flash',
  turnDetection: 'vad',
  ttsVoice: 'es-US-Neural2-A',
};

/* Triage por voz del paciente (config/agenteVozPaciente). Es el doc que lee el
   agente Python `triage-voz`. Aquí se controla si está habilitado y el prompt
   (persona / comportamiento) del asistente de voz. */
interface TriageVozConfig {
  triageVozHabilitado: boolean;
  promptVoz: string;
  // Modo de voz: 'pipeline' (STT→Gemini→TTS) | 'realtime' (Grok Voice Agent).
  modoVoz: string;
  realtimeModelo: string; // solo realtime: 'grok-voice-think-fast-1.0' | 'grok-voice-fast-1.0'
  // Proveedores de voz SOLO del triage (no afectan a los otros agentes). Pipeline.
  sttProveedor: string; // 'google' | 'deepgram' | 'xai'
  sttProveedorRespaldo: string; // ídem + 'ninguno'
  sttIdiomaRespaldo: string;
  ttsProveedor: string; // 'google' | 'deepgram' | 'xai' | 'elevenlabs'
  ttsVoice: string;
  ttsProveedorRespaldo: string; // ídem + 'ninguno'
  ttsVoiceRespaldo: string;
  ttsIdioma: string; // idioma primario TTS (xai/google): 'es-MX' | 'es-ES' | 'es'
}

const MODO_VOZ_OPTIONS: { value: string; label: string }[] = [
  { value: 'pipeline', label: 'Pipeline (STT → Gemini → TTS)' },
  { value: 'realtime', label: 'Realtime — Grok Voice Agent (cerebro = Grok)' },
];

const REALTIME_MODELOS = ['grok-voice-think-fast-1.0', 'grok-voice-fast-1.0'];

/* Proveedores soportados por el agente de triage por voz (livekit-agent-paciente).
   La voz femenina en español recomendada por proveedor va en `vozHint`. */
const TRIAGE_STT_PROVEEDORES: ProveedorOption[] = [
  { value: 'google', label: 'Google' },
  { value: 'deepgram', label: 'Deepgram Nova-3', env: 'DEEPGRAM_API_KEY' },
  { value: 'xai', label: 'xAI Grok', env: 'XAI_API_KEY' },
];

const TRIAGE_TTS_PROVEEDORES: (ProveedorOption & { vozHint: string })[] = [
  { value: 'elevenlabs', label: 'ElevenLabs (más natural)', env: 'ELEVEN_API_KEY', vozHint: '' },
  { value: 'deepgram', label: 'Deepgram Aura-2', env: 'DEEPGRAM_API_KEY', vozHint: 'aura-2-selena-es' },
  { value: 'xai', label: 'xAI Grok', env: 'XAI_API_KEY', vozHint: 'Ara' },
  { value: 'google', label: 'Google', vozHint: 'es-US-Neural2-A' },
];

const TRIAGE_TTS_IDIOMAS = ['es-MX', 'es-ES', 'es'];

/* Plantilla por defecto del prompt del asistente de voz. Debe conservar los
   placeholders {datos_paciente} y {prompt_clinico}, que el agente reemplaza en
   tiempo de ejecución. Si se deja vacío, el agente usa su plantilla interna. */
const DEFAULT_PROMPT_VOZ = `Eres un asistente de salud del Ministerio de Salud de Panamá que atiende al paciente POR VOZ (una llamada telefónica real), en español.

DATOS DEL PACIENTE:
{datos_paciente}

A continuación tienes los CRITERIOS CLÍNICOS. Fueron escritos para un sistema de TEXTO que responde en JSON — de ellos toma ÚNICAMENTE el criterio médico (qué preguntar, banderas rojas, niveles de prioridad). Su formato NO aplica aquí.

========== CRITERIOS CLÍNICOS (solo el criterio médico) ==========
{prompt_clinico}
========== FIN DE LOS CRITERIOS ==========

⚠️ REGLAS DE SALIDA — ABSOLUTAS Y FINALES. ANULAN CUALQUIER FORMATO INDICADO ARRIBA:

1. Estás en una LLAMADA DE VOZ. Tu respuesta es EXACTAMENTE lo que se va a decir en voz alta y nada más. JAMÁS produzcas JSON ni texto estructurado.
2. PROHIBIDO que tu respuesta contenga símbolos de JSON (llaves, corchetes, comillas como delimitador, dos puntos de campo) ni las palabras-campo "pregunta", "sugerencias", "esUltimaPregunta", "emergenciaDetectada", "esFinal", "clasificacion", "nivel", "true", "false". Si ibas a escribir algo así, NO lo hagas: di únicamente la frase hablada.
   Ejemplo MAL (NUNCA hagas esto): decir la palabra pregunta y sus campos.
   Ejemplo BIEN: ¿Desde cuándo se siente así?
3. NO eres un chat empático: eres un asistente de TRIAGE. Tu ÚNICA función es PREGUNTAR y CAPTURAR datos clínicos. PROHIBIDO ABSOLUTAMENTE hacer comentarios, acuses de recibo o frases de relleno. NUNCA digas cosas como: "entiendo", "eso debe ser molesto", "gracias por contarme", "lamento que…", "qué bien", "tranquilo", "estoy aquí para ayudarte", "con gusto le ayudo". Tampoco OPINES sobre los síntomas (no digas si algo es grave, serio, leve, preocupante, "normal", ni que "lleva mucho tiempo"). Después de CADA respuesta del paciente, pasa DIRECTO a la siguiente pregunta, sin preámbulo. El paciente ya sabe quién eres; no te presentes. Puedes usar su nombre muy de vez en cuando.
4. UNA sola pregunta por turno, CORTA y ABIERTA, sin preámbulos ("¿desde cuándo?" NO "entiendo, y ¿desde cuándo?"). NO ofrezcas listas de opciones ("¿leve, moderado o fuerte?"); pregunta abierto ("¿cómo es ese dolor?"). Solo si el paciente pide opciones le das un par de ejemplos. Números con palabras.
5. CIERRE — cuando ya tengas SUFICIENTE información (nunca digas la clasificación ni nombres de nivel; solo lenguaje natural):
   a) Primero DILE, hablando normal, que ya tienes toda la información que necesitas, y hazle un RESUMEN BREVE y hablado de lo que entendiste (motivo, síntoma principal, desde cuándo).
   b) Si el caso es APTO PARA TELECONSULTA (no es emergencia): pregúntale con claridad si desea que ENVÍES AHORA su solicitud de teleconsulta y ESPERA su respuesta. Si dice que SÍ, llama a finalizar_triage con confirmado=true. Si dice que NO, pregúntale si desea agregar algo; si insiste en no enviar, llama a finalizar_triage con confirmado=false.
   c) Si es una EMERGENCIA o el caso NO es apto para teleconsulta: dile con calma que por su caso es mejor acudir a un centro médico, y llama a finalizar_triage con emergencia=true (sin pedir confirmación).
   Deja que el sistema dé el mensaje final; tú solo conduces la conversación.`;

const DEFAULT_TRIAGE_VOZ: TriageVozConfig = {
  triageVozHabilitado: true,
  promptVoz: DEFAULT_PROMPT_VOZ,
  modoVoz: 'pipeline',
  realtimeModelo: 'grok-voice-think-fast-1.0',
  sttProveedor: 'deepgram',
  sttProveedorRespaldo: 'google',
  sttIdiomaRespaldo: 'es-US',
  ttsProveedor: 'google',
  ttsVoice: 'es-US-Neural2-A',
  ttsProveedorRespaldo: 'deepgram',
  ttsVoiceRespaldo: 'aura-2-selena-es',
  ttsIdioma: 'es-MX',
};

interface VadConfig {
  activationThreshold: number;
  minSpeechMs: number;
  minSilenceMs: number;
  prefixPaddingMs: number;
  finalMinConfidence: number;
}

const DEFAULT_VAD: VadConfig = {
  activationThreshold: 0.5,
  minSpeechMs: 100,
  minSilenceMs: 500,
  prefixPaddingMs: 300,
  finalMinConfidence: 0.4,
};

/* Horario de encendido de los agentes de voz (config/agentesVozWatchdog).
   Los 3 agentes (transcripción, agente de configuración por voz, triage por
   voz) corren en una VM que se APAGA a propósito fuera de este horario para
   ahorrar costos — no es una falla. Un proceso "vigilante" (watchdog) en esa
   misma VM revisa este documento cada pocos minutos y reinicia los agentes
   cuando corresponde, o de inmediato si se usa el botón "Iniciar ahora". */
interface AgentesVozWatchdogConfig {
  habilitado: boolean;
  /** "HH:mm", hora de Panamá (America/Panama). */
  horaInicio: string;
  /** Días en que aplica el horario. 0=domingo … 6=sábado. */
  diasActivos: number[];
  /** Minutos de margen tras la hora de inicio en los que el vigilante puede
   *  disparar el reinicio (evita perderlo si el chequeo cae unos minutos
   *  después de la hora exacta). */
  ventanaToleranciaMin: number;
}

const DEFAULT_AGENTES_VOZ_WATCHDOG: AgentesVozWatchdogConfig = {
  habilitado: true,
  horaInicio: '06:00',
  diasActivos: [0, 1, 2, 3, 4, 5, 6],
  ventanaToleranciaMin: 10,
};

/** Estado de lectura (lo escribe el vigilante en la VM, no se edita aquí). */
interface AgentesVozWatchdogStatus {
  ultimoReinicioAutomaticoEn: Timestamp | null;
  ultimoReinicioManualEn: Timestamp | null;
  reinicioManualSolicitadoEn: Timestamp | null;
  reinicioManualProcesadoEn: Timestamp | null;
}

const DIAS_SEMANA = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mié' },
  { value: 4, label: 'Jue' },
  { value: 5, label: 'Vie' },
  { value: 6, label: 'Sáb' },
];

function formatTs(ts: Timestamp | null | undefined): string {
  if (!ts) return 'Nunca';
  return new Intl.DateTimeFormat('es-PA', {
    timeZone: 'America/Panama',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(ts.toDate());
}

/* ─── Secciones (tabs) ────────────────────────────────────── */

type TabId = 'modelos' | 'llm' | 'livekit' | 'stt' | 'tts' | 'agenteVoz' | 'triageVoz' | 'vad' | 'watchdog';

const TABS: { id: TabId; label: string; icon: typeof Brain }[] = [
  { id: 'modelos', label: 'Modelos IA', icon: Brain },
  { id: 'llm', label: 'Parámetros LLM', icon: Sliders },
  { id: 'livekit', label: 'Proveedor de video', icon: Server },
  { id: 'stt', label: 'STT', icon: Mic },
  { id: 'tts', label: 'TTS', icon: Volume2 },
  { id: 'agenteVoz', label: 'Agente de voz', icon: Bot },
  { id: 'triageVoz', label: 'Triage por voz', icon: MessageCircle },
  { id: 'vad', label: 'Optimización (VAD)', icon: Waves },
  { id: 'watchdog', label: 'Horario de encendido', icon: Clock },
];

/* ─── Helpers ─────────────────────────────────────────────── */

function toNumber(value: string, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/* ─── Component ───────────────────────────────────────────── */

export default function ConfigIAPage() {
  const { addToast } = useToast();
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('modelos');

  // ─── Modelos ──
  const [geminiModels, setGeminiModels] = useState<string[]>(DEFAULT_MODELS);
  const [newModel, setNewModel] = useState('');
  const [savingModels, setSavingModels] = useState(false);

  // ─── Secretos (Gemini + LiveKit) ──
  const [geminiSecret, setGeminiSecret] = useState({ configured: false, masked: '' });
  const [geminiNew, setGeminiNew] = useState('');
  const [savingGemini, setSavingGemini] = useState(false);

  const [lkKeySecret, setLkKeySecret] = useState({ configured: false, masked: '' });
  const [lkKeyNew, setLkKeyNew] = useState('');
  const [savingLkKey, setSavingLkKey] = useState(false);

  const [lkSecretSecret, setLkSecretSecret] = useState({ configured: false, masked: '' });
  const [lkSecretNew, setLkSecretNew] = useState('');
  const [savingLkSecret, setSavingLkSecret] = useState(false);

  const [lkShKeySecret, setLkShKeySecret] = useState({ configured: false, masked: '' });
  const [lkShKeyNew, setLkShKeyNew] = useState('');
  const [savingLkShKey, setSavingLkShKey] = useState(false);

  const [lkShSecretSecret, setLkShSecretSecret] = useState({ configured: false, masked: '' });
  const [lkShSecretNew, setLkShSecretNew] = useState('');
  const [savingLkShSecret, setSavingLkShSecret] = useState(false);

  // ─── Parámetros LLM ──
  const [llmParams, setLlmParams] = useState<Record<LlmKey, LlmParam>>(DEFAULT_LLM_PARAMS);
  const [savingLlm, setSavingLlm] = useState(false);

  // ─── LiveKit ──
  const [livekit, setLivekit] = useState<LivekitConfig>(DEFAULT_LIVEKIT);
  const [savingLivekit, setSavingLivekit] = useState(false);

  // ─── STT ──
  const [stt, setStt] = useState<SttConfig>(DEFAULT_STT);
  const [savingStt, setSavingStt] = useState(false);

  // ─── TTS ──
  const [tts, setTts] = useState<TtsConfig>(DEFAULT_TTS);
  const [savingTts, setSavingTts] = useState(false);

  // ─── Agente de voz ──
  const [agenteVoz, setAgenteVoz] = useState<AgenteVozConfig>(DEFAULT_AGENTE_VOZ);
  const [savingAgenteVoz, setSavingAgenteVoz] = useState(false);

  // ─── Triage por voz (paciente) ──
  const [triageVoz, setTriageVoz] = useState<TriageVozConfig>(DEFAULT_TRIAGE_VOZ);
  const [savingTriageVoz, setSavingTriageVoz] = useState(false);

  // ─── Optimización (VAD) ──
  const [vad, setVad] = useState<VadConfig>(DEFAULT_VAD);
  const [savingVad, setSavingVad] = useState(false);

  // ─── Horario de encendido (watchdog de los agentes de voz) ──
  const [watchdog, setWatchdog] = useState<AgentesVozWatchdogConfig>(DEFAULT_AGENTES_VOZ_WATCHDOG);
  const [watchdogStatus, setWatchdogStatus] = useState<AgentesVozWatchdogStatus>({
    ultimoReinicioAutomaticoEn: null,
    ultimoReinicioManualEn: null,
    reinicioManualSolicitadoEn: null,
    reinicioManualProcesadoEn: null,
  });
  const [savingWatchdog, setSavingWatchdog] = useState(false);
  const [iniciandoManual, setIniciandoManual] = useState(false);

  // ─── Cambios sin guardar (una detección por sección) ────────
  // Cada sección tiene su propio estado y su propio botón Guardar, así que
  // lleva su propio badge inline en vez de una barra fija compartida.

  const { hayCambios: hayCambiosModelos, marcarGuardado: marcarGuardadoModelos } =
    useCambiosSinGuardar(geminiModels, { habilitado: !loading });
  const { hayCambios: hayCambiosLlm, marcarGuardado: marcarGuardadoLlm } =
    useCambiosSinGuardar(llmParams, { habilitado: !loading });
  const { hayCambios: hayCambiosLivekit, marcarGuardado: marcarGuardadoLivekit } =
    useCambiosSinGuardar(livekit, { habilitado: !loading });
  const { hayCambios: hayCambiosStt, marcarGuardado: marcarGuardadoStt } =
    useCambiosSinGuardar(stt, { habilitado: !loading });
  const { hayCambios: hayCambiosTts, marcarGuardado: marcarGuardadoTts } =
    useCambiosSinGuardar(tts, { habilitado: !loading });
  const { hayCambios: hayCambiosAgenteVoz, marcarGuardado: marcarGuardadoAgenteVoz } =
    useCambiosSinGuardar(agenteVoz, { habilitado: !loading });
  const { hayCambios: hayCambiosTriageVoz, marcarGuardado: marcarGuardadoTriageVoz } =
    useCambiosSinGuardar(triageVoz, { habilitado: !loading });
  const { hayCambios: hayCambiosVad, marcarGuardado: marcarGuardadoVad } =
    useCambiosSinGuardar(vad, { habilitado: !loading });
  const { hayCambios: hayCambiosWatchdog, marcarGuardado: marcarGuardadoWatchdog } =
    useCambiosSinGuardar(watchdog, { habilitado: !loading });

  // Línea base de las 9 secciones cuando termina la carga inicial (setLoading(false)
  // en el finally de loadDocs). Sin esto, los valores por defecto del primer render
  // contarían como "cambios" frente a lo que llega de Firestore.
  useEffect(() => {
    if (loading) return;
    marcarGuardadoModelos();
    marcarGuardadoLlm();
    marcarGuardadoLivekit();
    marcarGuardadoStt();
    marcarGuardadoTts();
    marcarGuardadoAgenteVoz();
    marcarGuardadoTriageVoz();
    marcarGuardadoVad();
    marcarGuardadoWatchdog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ─── Carga inicial ──────────────────────────────────────────

  useEffect(() => {
    async function loadDocs() {
      try {
        const [modelsSnap, llmSnap, lkSnap, sttSnap, ttsSnap, avSnap, tvSnap, vadSnap, wdSnap] = await Promise.all([
          getDoc(doc(db, 'config', 'aiModels')),
          getDoc(doc(db, 'config', 'llmParams')),
          getDoc(doc(db, 'config', 'livekit')),
          getDoc(doc(db, 'config', 'stt')),
          getDoc(doc(db, 'config', 'tts')),
          getDoc(doc(db, 'config', 'agenteVoz')),
          getDoc(doc(db, 'config', 'agenteVozPaciente')),
          getDoc(doc(db, 'config', 'vad')),
          getDoc(doc(db, 'config', 'agentesVozWatchdog')),
        ]);

        if (modelsSnap.exists()) {
          const data = modelsSnap.data() as { geminiModels?: string[] };
          if (Array.isArray(data.geminiModels) && data.geminiModels.length > 0) {
            setGeminiModels(data.geminiModels);
          }
        }

        if (llmSnap.exists()) {
          const data = llmSnap.data() as Partial<Record<LlmKey, Partial<LlmParam>>>;
          setLlmParams((prev) => {
            const merged = { ...prev };
            for (const { key } of LLM_KEYS) {
              merged[key] = { ...DEFAULT_LLM_PARAMS[key], ...(data[key] ?? {}) };
            }
            return merged;
          });
        }

        if (lkSnap.exists()) {
          setLivekit({ ...DEFAULT_LIVEKIT, ...(lkSnap.data() as Partial<LivekitConfig>) });
        }

        if (sttSnap.exists()) {
          setStt({ ...DEFAULT_STT, ...(sttSnap.data() as Partial<SttConfig>) });
        }

        if (ttsSnap.exists()) {
          setTts({ ...DEFAULT_TTS, ...(ttsSnap.data() as Partial<TtsConfig>) });
        }

        if (avSnap.exists()) {
          setAgenteVoz({ ...DEFAULT_AGENTE_VOZ, ...(avSnap.data() as Partial<AgenteVozConfig>) });
        }

        if (tvSnap.exists()) {
          const data = tvSnap.data() as Partial<TriageVozConfig>;
          setTriageVoz({
            // Habilitado por defecto: solo false si está explícitamente en false.
            triageVozHabilitado: data.triageVozHabilitado !== false,
            // Si nunca se configuró un prompt, mostramos el default editable.
            promptVoz: data.promptVoz && data.promptVoz.trim() ? data.promptVoz : DEFAULT_PROMPT_VOZ,
            modoVoz: data.modoVoz || DEFAULT_TRIAGE_VOZ.modoVoz,
            realtimeModelo: data.realtimeModelo || DEFAULT_TRIAGE_VOZ.realtimeModelo,
            sttProveedor: data.sttProveedor || DEFAULT_TRIAGE_VOZ.sttProveedor,
            sttProveedorRespaldo: data.sttProveedorRespaldo || DEFAULT_TRIAGE_VOZ.sttProveedorRespaldo,
            sttIdiomaRespaldo: data.sttIdiomaRespaldo ?? DEFAULT_TRIAGE_VOZ.sttIdiomaRespaldo,
            ttsProveedor: data.ttsProveedor || DEFAULT_TRIAGE_VOZ.ttsProveedor,
            ttsVoice: data.ttsVoice || DEFAULT_TRIAGE_VOZ.ttsVoice,
            ttsProveedorRespaldo: data.ttsProveedorRespaldo || DEFAULT_TRIAGE_VOZ.ttsProveedorRespaldo,
            ttsVoiceRespaldo: data.ttsVoiceRespaldo ?? DEFAULT_TRIAGE_VOZ.ttsVoiceRespaldo,
            ttsIdioma: data.ttsIdioma || DEFAULT_TRIAGE_VOZ.ttsIdioma,
          });
        }

        if (vadSnap.exists()) {
          setVad({ ...DEFAULT_VAD, ...(vadSnap.data() as Partial<VadConfig>) });
        }

        if (wdSnap.exists()) {
          const data = wdSnap.data() as Partial<AgentesVozWatchdogConfig> & Partial<AgentesVozWatchdogStatus>;
          setWatchdog({
            habilitado: data.habilitado !== false,
            horaInicio: data.horaInicio || DEFAULT_AGENTES_VOZ_WATCHDOG.horaInicio,
            diasActivos: Array.isArray(data.diasActivos) && data.diasActivos.length > 0
              ? data.diasActivos
              : DEFAULT_AGENTES_VOZ_WATCHDOG.diasActivos,
            ventanaToleranciaMin: data.ventanaToleranciaMin ?? DEFAULT_AGENTES_VOZ_WATCHDOG.ventanaToleranciaMin,
          });
          setWatchdogStatus({
            ultimoReinicioAutomaticoEn: data.ultimoReinicioAutomaticoEn ?? null,
            ultimoReinicioManualEn: data.ultimoReinicioManualEn ?? null,
            reinicioManualSolicitadoEn: data.reinicioManualSolicitadoEn ?? null,
            reinicioManualProcesadoEn: data.reinicioManualProcesadoEn ?? null,
          });
        }
      } catch (err) {
        console.error('Error cargando config IA:', err);
        addToast('error', 'No se pudo cargar toda la configuración de IA.');
      } finally {
        setLoading(false);
      }
    }

    async function loadSecrets() {
      const load = async (
        nombre: string,
        setter: (v: { configured: boolean; masked: string }) => void,
      ) => {
        try {
          const res = await obtenerEstadoSecretoFn({ nombre });
          setter({ configured: res.data.configured, masked: res.data.masked });
        } catch (err) {
          console.error(`Error consultando secreto ${nombre}:`, err);
        }
      };
      await Promise.all([
        load('GEMINI_API_KEY', setGeminiSecret),
        load('LIVEKIT_API_KEY', setLkKeySecret),
        load('LIVEKIT_API_SECRET', setLkSecretSecret),
        load('LIVEKIT_SELFHOSTED_API_KEY', setLkShKeySecret),
        load('LIVEKIT_SELFHOSTED_API_SECRET', setLkShSecretSecret),
      ]);
    }

    loadDocs();
    loadSecrets();
  }, [addToast]);

  // ─── Auditoría común ──────────────────────────────────────

  const audit = () => ({
    actualizadoEn: serverTimestamp(),
    actualizadoPor: uid,
  });

  // ─── Guardar: Modelos ─────────────────────────────────────

  const handleSaveModels = async () => {
    if (geminiModels.length === 0) {
      addToast('error', 'Debe haber al menos un modelo.');
      return;
    }
    setSavingModels(true);
    try {
      await setDoc(doc(db, 'config', 'aiModels'), { geminiModels, ...audit() }, { merge: true });
      marcarGuardadoModelos();
      addToast('success', 'Modelos guardados. Los cambios se aplican en ~5 minutos.');
    } catch (err) {
      console.error('Error guardando modelos:', err);
      addToast('error', 'Error al guardar los modelos.');
    } finally {
      setSavingModels(false);
    }
  };

  // ─── Guardar: secretos genéricos ──────────────────────────

  const saveSecret = async (
    nombre: string,
    valor: string,
    setSaving: (v: boolean) => void,
    setSecret: (v: { configured: boolean; masked: string }) => void,
    clearInput: () => void,
  ) => {
    if (!valor.trim() || valor.trim().length < 10) {
      addToast('error', 'El valor debe tener al menos 10 caracteres.');
      return;
    }
    setSaving(true);
    try {
      const res = await actualizarSecretoFn({ nombre, valor: valor.trim() });
      setSecret({ configured: true, masked: res.data.masked });
      clearInput();
      addToast('success', `${nombre} actualizado. Aplica en el próximo cold start.`);
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message || `Error al actualizar ${nombre}.`;
      addToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  // ─── Guardar: Parámetros LLM ──────────────────────────────

  const handleSaveLlm = async () => {
    for (const { key, label } of LLM_KEYS) {
      const p = llmParams[key];
      if (p.temperature < 0 || p.temperature > 1) {
        addToast('error', `La temperatura de "${label}" debe estar entre 0 y 1.`);
        return;
      }
      if (!Number.isInteger(p.maxOutputTokens) || p.maxOutputTokens <= 0) {
        addToast('error', `Los tokens de "${label}" deben ser un entero positivo.`);
        return;
      }
    }
    setSavingLlm(true);
    try {
      await setDoc(doc(db, 'config', 'llmParams'), { ...llmParams, ...audit() }, { merge: true });
      marcarGuardadoLlm();
      addToast('success', 'Parámetros LLM guardados. Aplican en ~5 minutos.');
    } catch (err) {
      console.error('Error guardando parámetros LLM:', err);
      addToast('error', 'Error al guardar los parámetros LLM.');
    } finally {
      setSavingLlm(false);
    }
  };

  // ─── Guardar: LiveKit ─────────────────────────────────────

  const handleSaveLivekit = async () => {
    const numeric: (keyof LivekitConfig)[] = [
      'maxParticipantes',
      'emptyTimeoutSeg',
      'departureTimeoutSeg',
      'maxDuracionSeg',
      'rateLimitVentanaMin',
      'rateLimitMax',
    ];
    for (const k of numeric) {
      const v = livekit[k] as number;
      if (!Number.isFinite(v) || v < 0) {
        addToast('error', `El valor de "${k}" no es válido.`);
        return;
      }
    }
    setSavingLivekit(true);
    try {
      await setDoc(doc(db, 'config', 'livekit'), { ...livekit, ...audit() }, { merge: true });
      marcarGuardadoLivekit();
      addToast('success', 'Configuración del proveedor de video guardada.');
    } catch (err) {
      console.error('Error guardando proveedor de video:', err);
      addToast('error', 'Error al guardar la configuración del proveedor de video.');
    } finally {
      setSavingLivekit(false);
    }
  };

  // ─── Guardar: STT ─────────────────────────────────────────

  const handleSaveStt = async () => {
    if (stt.minConfidence < 0 || stt.minConfidence > 1) {
      addToast('error', 'La confianza mínima debe estar entre 0 y 1.');
      return;
    }
    if (!Number.isFinite(stt.sampleRate) || stt.sampleRate <= 0) {
      addToast('error', 'El sample rate no es válido.');
      return;
    }
    setSavingStt(true);
    try {
      await setDoc(doc(db, 'config', 'stt'), { ...stt, ...audit() }, { merge: true });
      marcarGuardadoStt();
      addToast('success', 'Configuración STT guardada. Aplica en la próxima llamada.');
    } catch (err) {
      console.error('Error guardando STT:', err);
      addToast('error', 'Error al guardar la configuración STT.');
    } finally {
      setSavingStt(false);
    }
  };

  // ─── Guardar: TTS ─────────────────────────────────────────

  const handleSaveTts = async () => {
    setSavingTts(true);
    try {
      await setDoc(doc(db, 'config', 'tts'), { ...tts, ...audit() }, { merge: true });
      marcarGuardadoTts();
      addToast('success', 'Configuración TTS guardada. Aplica en la próxima llamada.');
    } catch (err) {
      console.error('Error guardando TTS:', err);
      addToast('error', 'Error al guardar la configuración TTS.');
    } finally {
      setSavingTts(false);
    }
  };

  // ─── Guardar: Agente de voz ───────────────────────────────

  const handleSaveAgenteVoz = async () => {
    setSavingAgenteVoz(true);
    try {
      await setDoc(doc(db, 'config', 'agenteVoz'), { ...agenteVoz, ...audit() }, { merge: true });
      marcarGuardadoAgenteVoz();
      addToast('success', 'Agente de voz guardado. Aplica en la próxima llamada.');
    } catch (err) {
      console.error('Error guardando agente de voz:', err);
      addToast('error', 'Error al guardar el agente de voz.');
    } finally {
      setSavingAgenteVoz(false);
    }
  };

  // ─── Guardar: Triage por voz (paciente) ───────────────────

  const handleSaveTriageVoz = async () => {
    setSavingTriageVoz(true);
    try {
      // merge → NO pisa llm* que también viven en este doc.
      await setDoc(
        doc(db, 'config', 'agenteVozPaciente'),
        {
          triageVozHabilitado: triageVoz.triageVozHabilitado,
          promptVoz: triageVoz.promptVoz.trim(),
          modoVoz: triageVoz.modoVoz,
          realtimeModelo: triageVoz.realtimeModelo,
          sttProveedor: triageVoz.sttProveedor,
          sttProveedorRespaldo: triageVoz.sttProveedorRespaldo,
          sttIdiomaRespaldo: triageVoz.sttIdiomaRespaldo.trim(),
          ttsProveedor: triageVoz.ttsProveedor,
          ttsVoice: triageVoz.ttsVoice.trim(),
          ttsProveedorRespaldo: triageVoz.ttsProveedorRespaldo,
          ttsVoiceRespaldo: triageVoz.ttsVoiceRespaldo.trim(),
          ttsIdioma: triageVoz.ttsIdioma,
          ...audit(),
        },
        { merge: true },
      );
      marcarGuardadoTriageVoz();
      addToast('success', 'Triage por voz guardado. Aplica en la próxima sesión.');
    } catch (err) {
      console.error('Error guardando triage por voz:', err);
      addToast('error', 'Error al guardar el triage por voz.');
    } finally {
      setSavingTriageVoz(false);
    }
  };

  // ─── Guardar: Optimización (VAD) ──────────────────────────

  const handleSaveVad = async () => {
    if (vad.activationThreshold < 0 || vad.activationThreshold > 1) {
      addToast('error', 'La sensibilidad del VAD debe estar entre 0 y 1.');
      return;
    }
    if (vad.finalMinConfidence < 0 || vad.finalMinConfidence > 1) {
      addToast('error', 'El umbral de confianza final debe estar entre 0 y 1.');
      return;
    }
    const msFields: { key: keyof VadConfig; label: string }[] = [
      { key: 'minSpeechMs', label: 'Duración mínima de voz' },
      { key: 'minSilenceMs', label: 'Silencio mínimo (hangover)' },
      { key: 'prefixPaddingMs', label: 'Pre-roll' },
    ];
    for (const { key, label } of msFields) {
      const v = vad[key];
      if (!Number.isInteger(v) || v <= 0) {
        addToast('error', `"${label}" debe ser un entero positivo (ms).`);
        return;
      }
    }
    setSavingVad(true);
    try {
      await setDoc(doc(db, 'config', 'vad'), { ...vad, ...audit() }, { merge: true });
      marcarGuardadoVad();
      addToast('success', 'Optimización (VAD) guardada. Aplica en la próxima llamada.');
    } catch (err) {
      console.error('Error guardando VAD:', err);
      addToast('error', 'Error al guardar la optimización (VAD).');
    } finally {
      setSavingVad(false);
    }
  };

  // ─── Guardar: Horario de encendido (watchdog) ─────────────

  const handleSaveWatchdog = async () => {
    if (!/^\d{2}:\d{2}$/.test(watchdog.horaInicio)) {
      addToast('error', 'La hora de inicio debe tener el formato HH:mm (ej. 06:00).');
      return;
    }
    if (watchdog.diasActivos.length === 0) {
      addToast('error', 'Seleccione al menos un día.');
      return;
    }
    setSavingWatchdog(true);
    try {
      await setDoc(
        doc(db, 'config', 'agentesVozWatchdog'),
        {
          habilitado: watchdog.habilitado,
          horaInicio: watchdog.horaInicio,
          diasActivos: watchdog.diasActivos,
          ventanaToleranciaMin: watchdog.ventanaToleranciaMin,
          ...audit(),
        },
        { merge: true },
      );
      marcarGuardadoWatchdog();
      addToast('success', 'Horario de encendido guardado. El vigilante lo revisa cada pocos minutos.');
    } catch (err) {
      console.error('Error guardando horario de encendido:', err);
      addToast('error', 'Error al guardar el horario de encendido.');
    } finally {
      setSavingWatchdog(false);
    }
  };

  const toggleDiaActivo = (dia: number) => {
    setWatchdog((c) => ({
      ...c,
      diasActivos: c.diasActivos.includes(dia)
        ? c.diasActivos.filter((d) => d !== dia)
        : [...c.diasActivos, dia].sort(),
    }));
  };

  /** Botón "Iniciar ahora": pide al vigilante que reinicie los agentes de
   *  inmediato, sin esperar el horario programado. El vigilante en la VM
   *  revisa `reinicioManualSolicitadoEn` cada pocos minutos y, al procesarlo,
   *  actualiza `ultimoReinicioManualEn` — por eso el estado de abajo puede
   *  tardar unos minutos en reflejar el resultado. */
  const handleIniciarManual = async () => {
    setIniciandoManual(true);
    try {
      const solicitadoEn = Timestamp.now();
      await setDoc(
        doc(db, 'config', 'agentesVozWatchdog'),
        { reinicioManualSolicitadoEn: solicitadoEn, ...audit() },
        { merge: true },
      );
      setWatchdogStatus((s) => ({ ...s, reinicioManualSolicitadoEn: solicitadoEn }));
      addToast('success', 'Solicitud enviada. Los agentes deberían encender en los próximos minutos.');
    } catch (err) {
      console.error('Error solicitando inicio manual:', err);
      addToast('error', 'No se pudo enviar la solicitud de inicio.');
    } finally {
      setIniciandoManual(false);
    }
  };

  // ─── Model helpers ────────────────────────────────────────

  const addModel = () => {
    const model = newModel.trim();
    if (model && !geminiModels.includes(model)) {
      setGeminiModels((prev) => [...prev, model]);
      setNewModel('');
    }
  };

  const removeModel = (idx: number) => {
    if (geminiModels.length <= 1) return;
    setGeminiModels((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    setGeminiModels((prev) => {
      const copy = [...prev];
      [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
      return copy;
    });
  };

  // ─── Reusable: secret block ───────────────────────────────

  const renderSecret = (
    label: string,
    secret: { configured: boolean; masked: string },
    newValue: string,
    setNewValue: (v: string) => void,
    saving: boolean,
    onSave: () => void,
  ) => (
    <div className={styles.secretBlock}>
      <span className={styles.secretLabel}>{label}</span>
      <div
        className={`${styles.apiKeyStatus} ${secret.configured ? styles.apiKeyStatusConfigured : ''}`}
      >
        <span className={styles.apiKeyStatusDot} />
        <span className={styles.apiKeyStatusText}>
          {secret.configured ? 'Configurado' : 'No configurado'}
        </span>
        {secret.masked && <span className={styles.apiKeyMasked}>{secret.masked}</span>}
      </div>
      <div className={styles.apiKeyForm}>
        <input
          type="password"
          className={styles.apiKeyInput}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder={secret.configured ? 'Nuevo valor para reemplazar…' : 'Ingrese el valor…'}
        />
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={!newValue.trim() || newValue.trim().length < 10 || saving}
          onClick={onSave}
        >
          <Key size={14} />
          {saving ? 'Guardando…' : secret.configured ? 'Actualizar' : 'Guardar'}
        </button>
      </div>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────

  if (loading) {
    return (
      <MainLayout>
        <div className={styles.loading}>
          <Loader2 size={20} className={styles.spinner} />
          Cargando configuración de IA...
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <Brain size={22} />
          <h1 className={styles.title}>Configuración de IA y Video</h1>
        </div>

        {/* Tabs */}
        <nav className={styles.tabs} role="tablist" aria-label="Secciones de configuración">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeTab === id}
              className={`${styles.tab} ${activeTab === id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </nav>

        <div className={styles.sections}>
          {/* ═══════════════ MODELOS IA ═══════════════ */}
          {activeTab === 'modelos' && (
            <>
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                  <Key size={16} className={styles.titleIcon} />
                  API Key de Gemini
                </h2>
                <div className={styles.sectionBody}>
                  <p className={styles.sectionDescription}>
                    Clave de API de Google Gemini utilizada por todas las funciones de IA del
                    sistema. Se almacena de forma segura en Secret Manager y nunca se muestra
                    completa.
                  </p>
                  {renderSecret(
                    'GEMINI_API_KEY',
                    geminiSecret,
                    geminiNew,
                    setGeminiNew,
                    savingGemini,
                    () =>
                      saveSecret(
                        'GEMINI_API_KEY',
                        geminiNew,
                        setSavingGemini,
                        setGeminiSecret,
                        () => setGeminiNew(''),
                      ),
                  )}
                  <div className={styles.infoBox}>
                    <Info size={14} />
                    <span>
                      Los cambios toman efecto en el próximo cold start de las Cloud Functions
                      (generalmente en pocos minutos).
                    </span>
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Modelos de Gemini</h2>
                <div className={styles.sectionBody}>
                  <p className={styles.sectionDescription}>
                    Lista ordenada de modelos. Si el primer modelo falla, se intenta con el
                    siguiente (fallback). Los cambios se aplican en aproximadamente 5 minutos.
                  </p>

                  <div className={styles.modelsList}>
                    {geminiModels.map((model, idx) => (
                      <div
                        key={idx}
                        className={`${styles.modelItem} ${idx === 0 ? styles.modelItemPrimary : ''}`}
                      >
                        <span className={styles.modelName}>{model}</span>
                        <span
                          className={`${styles.modelBadge} ${
                            idx === 0 ? styles.modelBadgePrimary : styles.modelBadgeFallback
                          }`}
                        >
                          {idx === 0 ? 'Principal' : `Fallback ${idx}`}
                        </span>
                        {idx > 0 && (
                          <button
                            type="button"
                            className={styles.modelBtn}
                            onClick={() => moveUp(idx)}
                            title="Subir prioridad"
                          >
                            <ArrowUp size={14} />
                          </button>
                        )}
                        <button
                          type="button"
                          className={`${styles.modelBtn} ${styles.modelBtnDanger}`}
                          onClick={() => removeModel(idx)}
                          disabled={geminiModels.length <= 1}
                          title="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className={styles.addModelRow}>
                    <input
                      type="text"
                      className={styles.addModelInput}
                      value={newModel}
                      onChange={(e) => setNewModel(e.target.value)}
                      placeholder="Ej: gemini-2.0-flash"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addModel();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      disabled={!newModel.trim() || geminiModels.includes(newModel.trim())}
                      onClick={addModel}
                    >
                      <Plus size={14} />
                      Agregar
                    </button>
                  </div>

                  <div className={styles.sectionActions} style={{ alignItems: 'center', gap: 10 }}>
                    <BadgeCambiosSinGuardar visible={hayCambiosModelos} />
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      disabled={savingModels}
                      onClick={handleSaveModels}
                    >
                      <Save size={16} />
                      {savingModels ? 'Guardando…' : 'Guardar modelos'}
                    </button>
                  </div>
                </div>
              </section>
            </>
          )}

          {/* ═══════════════ PARÁMETROS LLM ═══════════════ */}
          {activeTab === 'llm' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <Sliders size={16} className={styles.titleIcon} />
                Parámetros LLM por función
              </h2>
              <div className={styles.sectionBody}>
                <p className={styles.sectionDescription}>
                  Temperatura (0–1, creatividad de la respuesta) y máximo de tokens de salida para
                  cada función de IA. Los cambios se aplican en aproximadamente 5 minutos.
                </p>

                <div className={styles.paramTable}>
                  <div className={`${styles.paramRow} ${styles.paramHeader}`}>
                    <span>Función</span>
                    <span>Temperature</span>
                    <span>Max tokens</span>
                  </div>
                  {LLM_KEYS.map(({ key, label }) => (
                    <div key={key} className={styles.paramRow}>
                      <span className={styles.paramLabel}>{label}</span>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        className={styles.numInput}
                        value={llmParams[key].temperature}
                        onChange={(e) =>
                          setLlmParams((prev) => ({
                            ...prev,
                            [key]: {
                              ...prev[key],
                              temperature: toNumber(e.target.value, prev[key].temperature),
                            },
                          }))
                        }
                      />
                      <input
                        type="number"
                        min={1}
                        step={1}
                        className={styles.numInput}
                        value={llmParams[key].maxOutputTokens}
                        onChange={(e) =>
                          setLlmParams((prev) => ({
                            ...prev,
                            [key]: {
                              ...prev[key],
                              maxOutputTokens: toNumber(e.target.value, prev[key].maxOutputTokens),
                            },
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>

                <div className={styles.sectionActions} style={{ alignItems: 'center', gap: 10 }}>
                  <BadgeCambiosSinGuardar visible={hayCambiosLlm} />
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={savingLlm}
                    onClick={handleSaveLlm}
                  >
                    <Save size={16} />
                    {savingLlm ? 'Guardando…' : 'Guardar parámetros'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ═══════════════ PROVEEDOR DE VIDEO ═══════════════ */}
          {activeTab === 'livekit' && (
            <>
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                  <Server size={16} className={styles.titleIcon} />
                  Proveedor de video
                </h2>
                <div className={styles.sectionBody}>
                  <p className={styles.sectionDescription}>
                    Selecciona el proveedor del servidor de video en tiempo real (WebRTC) y
                    configura sus credenciales. Los parámetros de sala/token son compartidos por
                    ambos modos.
                  </p>

                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Proveedor de video</label>
                    <div
                      className={styles.segmented}
                      role="radiogroup"
                      aria-label="Proveedor de video"
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={livekit.proveedor === 'cloud'}
                        className={`${styles.segment} ${
                          livekit.proveedor === 'cloud' ? styles.segmentActive : ''
                        }`}
                        onClick={() => setLivekit((c) => ({ ...c, proveedor: 'cloud' }))}
                      >
                        <Cloud size={15} />
                        LiveKit Cloud
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={livekit.proveedor === 'selfhosted'}
                        className={`${styles.segment} ${
                          livekit.proveedor === 'selfhosted' ? styles.segmentActive : ''
                        }`}
                        onClick={() => setLivekit((c) => ({ ...c, proveedor: 'selfhosted' }))}
                      >
                        <HardDrive size={15} />
                        Servidor propio (autohospedado)
                      </button>
                    </div>
                  </div>

                  <div className={styles.caveatBox}>
                    <AlertTriangle size={14} />
                    <span>
                      El cambio de proveedor afecta la generación de tokens del cliente. Los agentes
                      de transcripción y de voz deben apuntar también al servidor seleccionado
                      (actualizar su LIVEKIT_URL y redeploy).
                    </span>
                  </div>

                  {/* URL — Cloud */}
                  {livekit.proveedor === 'cloud' && (
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>URL de LiveKit Cloud</label>
                      <input
                        type="text"
                        className={styles.textInput}
                        value={livekit.url}
                        onChange={(e) => setLivekit((c) => ({ ...c, url: e.target.value }))}
                        placeholder="wss://tu-proyecto.livekit.cloud"
                      />
                      <span className={styles.fieldHint}>
                        Vacío = usa el secret LIVEKIT_URL.
                      </span>
                    </div>
                  )}

                  {/* URL — Self-hosted */}
                  {livekit.proveedor === 'selfhosted' && (
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>URL del servidor autohospedado</label>
                      <input
                        type="text"
                        className={styles.textInput}
                        value={livekit.urlSelfHosted}
                        onChange={(e) =>
                          setLivekit((c) => ({ ...c, urlSelfHosted: e.target.value }))
                        }
                        placeholder="wss://mi-servidor:7880"
                      />
                      <span className={styles.fieldHint}>
                        Si no se configura, se usa el de LiveKit Cloud.
                      </span>
                    </div>
                  )}

                  <div className={styles.grid}>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>TTL token médico</label>
                      <input
                        type="text"
                        className={styles.textInput}
                        value={livekit.tokenTtlMedico}
                        onChange={(e) =>
                          setLivekit((c) => ({ ...c, tokenTtlMedico: e.target.value }))
                        }
                        placeholder="2h"
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Máx. participantes</label>
                      <input
                        type="number"
                        min={1}
                        className={styles.textInput}
                        value={livekit.maxParticipantes}
                        onChange={(e) =>
                          setLivekit((c) => ({
                            ...c,
                            maxParticipantes: toNumber(e.target.value, c.maxParticipantes),
                          }))
                        }
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Empty timeout (seg)</label>
                      <input
                        type="number"
                        min={0}
                        className={styles.textInput}
                        value={livekit.emptyTimeoutSeg}
                        onChange={(e) =>
                          setLivekit((c) => ({
                            ...c,
                            emptyTimeoutSeg: toNumber(e.target.value, c.emptyTimeoutSeg),
                          }))
                        }
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Departure timeout (seg)</label>
                      <input
                        type="number"
                        min={0}
                        className={styles.textInput}
                        value={livekit.departureTimeoutSeg}
                        onChange={(e) =>
                          setLivekit((c) => ({
                            ...c,
                            departureTimeoutSeg: toNumber(e.target.value, c.departureTimeoutSeg),
                          }))
                        }
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Duración máx. (seg)</label>
                      <input
                        type="number"
                        min={0}
                        className={styles.textInput}
                        value={livekit.maxDuracionSeg}
                        onChange={(e) =>
                          setLivekit((c) => ({
                            ...c,
                            maxDuracionSeg: toNumber(e.target.value, c.maxDuracionSeg),
                          }))
                        }
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Rate limit — ventana (min)</label>
                      <input
                        type="number"
                        min={1}
                        className={styles.textInput}
                        value={livekit.rateLimitVentanaMin}
                        onChange={(e) =>
                          setLivekit((c) => ({
                            ...c,
                            rateLimitVentanaMin: toNumber(e.target.value, c.rateLimitVentanaMin),
                          }))
                        }
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Rate limit — máx. solicitudes</label>
                      <input
                        type="number"
                        min={1}
                        className={styles.textInput}
                        value={livekit.rateLimitMax}
                        onChange={(e) =>
                          setLivekit((c) => ({
                            ...c,
                            rateLimitMax: toNumber(e.target.value, c.rateLimitMax),
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className={styles.sectionActions} style={{ alignItems: 'center', gap: 10 }}>
                    <BadgeCambiosSinGuardar visible={hayCambiosLivekit} />
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      disabled={savingLivekit}
                      onClick={handleSaveLivekit}
                    >
                      <Save size={16} />
                      {savingLivekit ? 'Guardando…' : 'Guardar proveedor de video'}
                    </button>
                  </div>
                </div>
              </section>

              {/* Credenciales — Cloud */}
              {livekit.proveedor === 'cloud' && (
                <section className={styles.section}>
                  <h2 className={styles.sectionTitle}>
                    <Key size={16} className={styles.titleIcon} />
                    Credenciales LiveKit Cloud
                  </h2>
                  <div className={styles.sectionBody}>
                    <p className={styles.sectionDescription}>
                      Claves de API de LiveKit Cloud almacenadas en Secret Manager. Aplican en el
                      próximo cold start de las Cloud Functions.
                    </p>
                    {renderSecret(
                      'LIVEKIT_API_KEY',
                      lkKeySecret,
                      lkKeyNew,
                      setLkKeyNew,
                      savingLkKey,
                      () =>
                        saveSecret('LIVEKIT_API_KEY', lkKeyNew, setSavingLkKey, setLkKeySecret, () =>
                          setLkKeyNew(''),
                        ),
                    )}
                    {renderSecret(
                      'LIVEKIT_API_SECRET',
                      lkSecretSecret,
                      lkSecretNew,
                      setLkSecretNew,
                      savingLkSecret,
                      () =>
                        saveSecret(
                          'LIVEKIT_API_SECRET',
                          lkSecretNew,
                          setSavingLkSecret,
                          setLkSecretSecret,
                          () => setLkSecretNew(''),
                        ),
                    )}
                  </div>
                </section>
              )}

              {/* Credenciales — Self-hosted */}
              {livekit.proveedor === 'selfhosted' && (
                <section className={styles.section}>
                  <h2 className={styles.sectionTitle}>
                    <Key size={16} className={styles.titleIcon} />
                    Credenciales del servidor autohospedado
                  </h2>
                  <div className={styles.sectionBody}>
                    <p className={styles.sectionDescription}>
                      Claves de API del servidor LiveKit autohospedado, almacenadas en Secret
                      Manager. Aplican en el próximo cold start de las Cloud Functions.
                    </p>
                    {renderSecret(
                      'LIVEKIT_SELFHOSTED_API_KEY',
                      lkShKeySecret,
                      lkShKeyNew,
                      setLkShKeyNew,
                      savingLkShKey,
                      () =>
                        saveSecret(
                          'LIVEKIT_SELFHOSTED_API_KEY',
                          lkShKeyNew,
                          setSavingLkShKey,
                          setLkShKeySecret,
                          () => setLkShKeyNew(''),
                        ),
                    )}
                    {renderSecret(
                      'LIVEKIT_SELFHOSTED_API_SECRET',
                      lkShSecretSecret,
                      lkShSecretNew,
                      setLkShSecretNew,
                      savingLkShSecret,
                      () =>
                        saveSecret(
                          'LIVEKIT_SELFHOSTED_API_SECRET',
                          lkShSecretNew,
                          setSavingLkShSecret,
                          setLkShSecretSecret,
                          () => setLkShSecretNew(''),
                        ),
                    )}
                    <div className={styles.infoBox}>
                      <Info size={14} />
                      <span>Si un valor no se configura, se usa el de LiveKit Cloud.</span>
                    </div>
                  </div>
                </section>
              )}
            </>
          )}

          {/* ═══════════════ STT ═══════════════ */}
          {activeTab === 'stt' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <Mic size={16} className={styles.titleIcon} />
                Speech-to-Text (transcripción)
              </h2>
              <div className={styles.sectionBody}>
                <p className={styles.sectionDescription}>
                  Configuración del reconocimiento de voz usado por los agentes de transcripción.
                </p>
                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>Los cambios aplican en la próxima llamada / sesión del agente.</span>
                </div>
                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>{NOTA_PROVEEDORES}</span>
                </div>

                <div className={styles.grid}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Proveedor</label>
                    <select
                      className={styles.textInput}
                      value={stt.proveedor}
                      onChange={(e) => {
                        const nuevo = e.target.value;
                        // Si el nuevo principal era el respaldo, se intercambian.
                        setStt((c) => ({
                          ...c,
                          proveedor: nuevo,
                          ...(c.proveedorRespaldo === nuevo
                            ? { proveedorRespaldo: c.proveedor, respaldoModelo: c.modelo, respaldoIdioma: c.idioma,
                                modelo: c.respaldoModelo, idioma: c.respaldoIdioma }
                            : {}),
                        }));
                      }}
                    >
                      {STT_PROVEEDORES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    {envFor(STT_PROVEEDORES, stt.proveedor) && (
                      <span className={styles.fieldHint}>
                        Requiere la variable de entorno {envFor(STT_PROVEEDORES, stt.proveedor)} en
                        el deploy del agente.
                      </span>
                    )}
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Idioma</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      value={stt.idioma}
                      onChange={(e) => setStt((c) => ({ ...c, idioma: e.target.value }))}
                      placeholder="es-US"
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Modelo</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      value={stt.modelo}
                      onChange={(e) => setStt((c) => ({ ...c, modelo: e.target.value }))}
                      placeholder={modeloHintFor(STT_PROVEEDORES, stt.proveedor) || 'default'}
                    />
                    <span className={styles.fieldHint}>
                      Sugerido para {stt.proveedor}:{' '}
                      {modeloHintFor(STT_PROVEEDORES, stt.proveedor) || 'default'}.
                    </span>
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Proveedor de respaldo</label>
                    <select
                      className={styles.textInput}
                      value={stt.proveedorRespaldo}
                      onChange={(e) => {
                        const nuevo = e.target.value;
                        setStt((c) => ({
                          ...c,
                          proveedorRespaldo: nuevo,
                          respaldoModelo: modeloHintFor(STT_PROVEEDORES, nuevo),
                          respaldoIdioma: nuevo === 'deepgram' ? 'multi' : nuevo === 'google' ? 'es-US' : '',
                        }));
                      }}
                    >
                      {opcionesRespaldo(STT_PROVEEDORES)
                        .filter((p) => p.value !== stt.proveedor)
                        .map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                    </select>
                    <span className={styles.fieldHint}>
                      Si el proveedor principal falla (sin saldo, llave rechazada o caído), la
                      transcripción sigue sola con este y queda una alerta.
                      {envFor(STT_PROVEEDORES, stt.proveedorRespaldo) &&
                        ` Requiere ${envFor(STT_PROVEEDORES, stt.proveedorRespaldo)} en el deploy del agente.`}
                    </span>
                  </div>
                  {stt.proveedorRespaldo !== 'ninguno' && (
                    <>
                      <div className={styles.field}>
                        <label className={styles.fieldLabel}>Idioma del respaldo</label>
                        <input
                          type="text"
                          className={styles.textInput}
                          value={stt.respaldoIdioma}
                          onChange={(e) => setStt((c) => ({ ...c, respaldoIdioma: e.target.value }))}
                          placeholder={stt.proveedorRespaldo === 'deepgram' ? 'multi' : 'es-US'}
                        />
                      </div>
                      <div className={styles.field}>
                        <label className={styles.fieldLabel}>Modelo del respaldo</label>
                        <input
                          type="text"
                          className={styles.textInput}
                          value={stt.respaldoModelo}
                          onChange={(e) => setStt((c) => ({ ...c, respaldoModelo: e.target.value }))}
                          placeholder={modeloHintFor(STT_PROVEEDORES, stt.proveedorRespaldo) || 'default'}
                        />
                      </div>
                    </>
                  )}
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Sample rate</label>
                    <input
                      type="number"
                      min={1}
                      className={styles.textInput}
                      value={stt.sampleRate}
                      onChange={(e) =>
                        setStt((c) => ({ ...c, sampleRate: toNumber(e.target.value, c.sampleRate) }))
                      }
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Confianza mínima (0–1)</label>
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      className={styles.textInput}
                      value={stt.minConfidence}
                      onChange={(e) =>
                        setStt((c) => ({
                          ...c,
                          minConfidence: toNumber(e.target.value, c.minConfidence),
                        }))
                      }
                    />
                  </div>
                </div>

                <div className={styles.toggleRow}>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={stt.interimResults}
                      onChange={(e) => setStt((c) => ({ ...c, interimResults: e.target.checked }))}
                    />
                    Resultados intermedios (interim results)
                  </label>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={stt.punctuate}
                      onChange={(e) => setStt((c) => ({ ...c, punctuate: e.target.checked }))}
                    />
                    Puntuación automática
                  </label>
                </div>

                <div className={styles.sectionActions} style={{ alignItems: 'center', gap: 10 }}>
                  <BadgeCambiosSinGuardar visible={hayCambiosStt} />
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={savingStt}
                    onClick={handleSaveStt}
                  >
                    <Save size={16} />
                    {savingStt ? 'Guardando…' : 'Guardar STT'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ═══════════════ TTS ═══════════════ */}
          {activeTab === 'tts' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <Volume2 size={16} className={styles.titleIcon} />
                Text-to-Speech (síntesis de voz)
              </h2>
              <div className={styles.sectionBody}>
                <p className={styles.sectionDescription}>
                  Configuración de la voz sintetizada del agente de voz.
                </p>
                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>Los cambios aplican en la próxima llamada / sesión del agente.</span>
                </div>
                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>{NOTA_PROVEEDORES}</span>
                </div>

                <div className={styles.grid}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Proveedor</label>
                    <select
                      className={styles.textInput}
                      value={tts.proveedor}
                      onChange={(e) => setTts((c) => ({ ...c, proveedor: e.target.value }))}
                    >
                      {TTS_PROVEEDORES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    {envFor(TTS_PROVEEDORES, tts.proveedor) && (
                      <span className={styles.fieldHint}>
                        Requiere la variable de entorno {envFor(TTS_PROVEEDORES, tts.proveedor)} en
                        el deploy del agente.
                      </span>
                    )}
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Idioma</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      value={tts.idioma}
                      onChange={(e) => setTts((c) => ({ ...c, idioma: e.target.value }))}
                      placeholder="es-US"
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Modelo (opcional)</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      value={tts.modelo}
                      onChange={(e) => setTts((c) => ({ ...c, modelo: e.target.value }))}
                      placeholder={modeloHintFor(TTS_PROVEEDORES, tts.proveedor) || 'default'}
                    />
                    <span className={styles.fieldHint}>
                      Sugerido para {tts.proveedor}:{' '}
                      {modeloHintFor(TTS_PROVEEDORES, tts.proveedor) || 'default'}.
                    </span>
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Voz</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      value={tts.voz}
                      onChange={(e) => setTts((c) => ({ ...c, voz: e.target.value }))}
                      placeholder="es-US-Neural2-A"
                    />
                  </div>
                </div>

                <div className={styles.toggleRow}>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={tts.useStreaming}
                      onChange={(e) => setTts((c) => ({ ...c, useStreaming: e.target.checked }))}
                    />
                    Usar streaming
                  </label>
                </div>

                <div className={styles.sectionActions} style={{ alignItems: 'center', gap: 10 }}>
                  <BadgeCambiosSinGuardar visible={hayCambiosTts} />
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={savingTts}
                    onClick={handleSaveTts}
                  >
                    <Save size={16} />
                    {savingTts ? 'Guardando…' : 'Guardar TTS'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ═══════════════ AGENTE DE VOZ ═══════════════ */}
          {activeTab === 'agenteVoz' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <Bot size={16} className={styles.titleIcon} />
                Agente de voz (configuración por voz)
              </h2>
              <div className={styles.sectionBody}>
                <p className={styles.sectionDescription}>
                  Modelo LLM y voz usados por el asistente de configuración por voz.
                </p>
                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>Los cambios aplican en la próxima llamada / sesión del agente.</span>
                </div>
                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>{NOTA_PROVEEDORES}</span>
                </div>

                <div className={styles.grid}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Proveedor LLM</label>
                    <select
                      className={styles.textInput}
                      value={agenteVoz.llmProveedor}
                      onChange={(e) =>
                        setAgenteVoz((c) => ({ ...c, llmProveedor: e.target.value }))
                      }
                    >
                      {LLM_PROVEEDORES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    {envFor(LLM_PROVEEDORES, agenteVoz.llmProveedor) && (
                      <span className={styles.fieldHint}>
                        Requiere la variable de entorno{' '}
                        {envFor(LLM_PROVEEDORES, agenteVoz.llmProveedor)} en el deploy del agente.
                      </span>
                    )}
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Modelo LLM</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      value={agenteVoz.llmModel}
                      onChange={(e) => setAgenteVoz((c) => ({ ...c, llmModel: e.target.value }))}
                      placeholder={
                        modeloHintFor(LLM_PROVEEDORES, agenteVoz.llmProveedor) || 'gemini-2.5-flash'
                      }
                    />
                    <span className={styles.fieldHint}>
                      Sugerido para {agenteVoz.llmProveedor}:{' '}
                      {modeloHintFor(LLM_PROVEEDORES, agenteVoz.llmProveedor) || 'gemini-2.5-flash'}.
                    </span>
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Detección de turno</label>
                    <select
                      className={styles.textInput}
                      value={agenteVoz.turnDetection}
                      onChange={(e) =>
                        setAgenteVoz((c) => ({ ...c, turnDetection: e.target.value }))
                      }
                    >
                      {TURN_DETECTION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <span className={styles.fieldHint}>
                      La detección semántica mejora el reconocimiento de fin de turno; corre en el
                      agente de voz.
                    </span>
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Voz TTS</label>
                    <input
                      type="text"
                      className={styles.textInput}
                      value={agenteVoz.ttsVoice}
                      onChange={(e) => setAgenteVoz((c) => ({ ...c, ttsVoice: e.target.value }))}
                      placeholder="es-US-Neural2-A"
                    />
                  </div>
                </div>

                <div className={styles.sectionActions} style={{ alignItems: 'center', gap: 10 }}>
                  <BadgeCambiosSinGuardar visible={hayCambiosAgenteVoz} />
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={savingAgenteVoz}
                    onClick={handleSaveAgenteVoz}
                  >
                    <Save size={16} />
                    {savingAgenteVoz ? 'Guardando…' : 'Guardar agente de voz'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ═══════════════ TRIAGE POR VOZ (PACIENTE) ═══════════════ */}
          {activeTab === 'triageVoz' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <MessageCircle size={16} className={styles.titleIcon} />
                Asistente de triage por voz (paciente)
              </h2>
              <div className={styles.sectionBody}>
                <p className={styles.sectionDescription}>
                  Asistente que permite al paciente hacer la pre-evaluación (triage) hablando desde
                  la app móvil. Aquí puede habilitarlo o deshabilitarlo, y editar su prompt
                  (personalidad y comportamiento en la llamada de voz).
                </p>
                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>Los cambios aplican en la próxima sesión de triage por voz.</span>
                </div>

                {/* Habilitar / deshabilitar */}
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>
                    <Power size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                    Estado del asistente
                  </label>
                  <div className={styles.toggleRow}>
                    <label className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={triageVoz.triageVozHabilitado}
                        onChange={(e) =>
                          setTriageVoz((c) => ({ ...c, triageVozHabilitado: e.target.checked }))
                        }
                      />
                      {triageVoz.triageVozHabilitado
                        ? 'Habilitado — el paciente ve el botón de micrófono en el triage'
                        : 'Deshabilitado — se oculta el triage por voz en la app'}
                    </label>
                  </div>
                  <span className={styles.fieldHint}>
                    Habilitado por defecto. Al deshabilitar, la app oculta el botón de voz y el
                    servidor rechaza nuevas sesiones de triage por voz.
                  </span>
                </div>

                {/* Modo de voz: pipeline vs realtime */}
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Modo de voz</label>
                  <select
                    className={styles.textInput}
                    value={triageVoz.modoVoz}
                    onChange={(e) => setTriageVoz((c) => ({ ...c, modoVoz: e.target.value }))}
                  >
                    {MODO_VOZ_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <span className={styles.fieldHint}>
                    {triageVoz.modoVoz === 'realtime'
                      ? 'Grok Voice Agent (speech-to-speech, como la página de x.ai): mejor conversación y prosodia. El cerebro es Grok — usa el prompt de voz como instrucciones; NO usa Gemini ni el STT/TTS de abajo. Requiere XAI_API_KEY.'
                      : 'Pipeline configurable: reconocimiento (STT) → Gemini (cerebro + prompt clínico) → voz (TTS). Usa los modelos de abajo.'}
                  </span>
                </div>

                {triageVoz.modoVoz === 'realtime' && (
                  <div className={styles.grid}>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Modelo realtime (Grok)</label>
                      <select
                        className={styles.textInput}
                        value={triageVoz.realtimeModelo}
                        onChange={(e) =>
                          setTriageVoz((c) => ({ ...c, realtimeModelo: e.target.value }))
                        }
                      >
                        {REALTIME_MODELOS.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <span className={styles.fieldHint}>
                        think-fast = razona un poco más; fast = menor latencia.
                      </span>
                    </div>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Voz (femenina)</label>
                      <input
                        type="text"
                        className={styles.textInput}
                        value={triageVoz.ttsVoice}
                        onChange={(e) => setTriageVoz((c) => ({ ...c, ttsVoice: e.target.value }))}
                        placeholder="Ara"
                      />
                      <span className={styles.fieldHint}>
                        Femeninas: Ara (cálida) o Eve (natural).
                      </span>
                    </div>
                  </div>
                )}

                {/* Proveedores de voz (STT / TTS) — solo del pipeline */}
                {triageVoz.modoVoz !== 'realtime' && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Modelos de voz (STT y TTS)</label>
                  <div className={styles.infoBox}>
                    <Info size={14} />
                    <span>
                      Elige el motor de reconocimiento (STT) y de voz (TTS) solo para el triage por
                      voz. El “cerebro” (Gemini + prompt clínico) no cambia. Las claves API se
                      configuran en el deploy del agente.
                    </span>
                  </div>
                  <div className={styles.grid}>
                    {/* STT */}
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Reconocimiento de voz (STT)</label>
                      <select
                        className={styles.textInput}
                        value={triageVoz.sttProveedor}
                        onChange={(e) => {
                          const nuevo = e.target.value;
                          setTriageVoz((c) => ({
                            ...c,
                            sttProveedor: nuevo,
                            ...(c.sttProveedorRespaldo === nuevo ? { sttProveedorRespaldo: c.sttProveedor } : {}),
                          }));
                        }}
                      >
                        {TRIAGE_STT_PROVEEDORES.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      {envFor(TRIAGE_STT_PROVEEDORES, triageVoz.sttProveedor) && (
                        <span className={styles.fieldHint}>
                          Requiere {envFor(TRIAGE_STT_PROVEEDORES, triageVoz.sttProveedor)} en el
                          deploy del agente.
                        </span>
                      )}
                    </div>

                    {/* STT respaldo */}
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Reconocimiento de respaldo</label>
                      <select
                        className={styles.textInput}
                        value={triageVoz.sttProveedorRespaldo}
                        onChange={(e) =>
                          setTriageVoz((c) => ({ ...c, sttProveedorRespaldo: e.target.value }))
                        }
                      >
                        {opcionesRespaldo(TRIAGE_STT_PROVEEDORES)
                          .filter((p) => p.value !== triageVoz.sttProveedor)
                          .map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                      </select>
                      <span className={styles.fieldHint}>
                        Si el principal falla, el triage sigue con este en la misma conversación.
                      </span>
                    </div>
                    {triageVoz.sttProveedorRespaldo !== 'ninguno' && (
                      <div className={styles.field}>
                        <label className={styles.fieldLabel}>Idioma del respaldo</label>
                        <input
                          type="text"
                          className={styles.textInput}
                          value={triageVoz.sttIdiomaRespaldo}
                          onChange={(e) =>
                            setTriageVoz((c) => ({ ...c, sttIdiomaRespaldo: e.target.value }))
                          }
                          placeholder={triageVoz.sttProveedorRespaldo === 'deepgram' ? 'es-419' : 'es-US'}
                        />
                      </div>
                    )}

                    {/* TTS proveedor */}
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Voz del asistente (TTS)</label>
                      <select
                        className={styles.textInput}
                        value={triageVoz.ttsProveedor}
                        onChange={(e) => {
                          const nuevo = e.target.value;
                          const hint =
                            TRIAGE_TTS_PROVEEDORES.find((p) => p.value === nuevo)?.vozHint || '';
                          // Al cambiar de proveedor, sugerimos su voz femenina por defecto.
                          // Si el nuevo principal era el respaldo, se intercambian.
                          setTriageVoz((c) => ({
                            ...c,
                            ttsProveedor: nuevo,
                            ttsVoice: c.ttsProveedorRespaldo === nuevo ? c.ttsVoiceRespaldo || hint : hint,
                            ...(c.ttsProveedorRespaldo === nuevo
                              ? { ttsProveedorRespaldo: c.ttsProveedor, ttsVoiceRespaldo: c.ttsVoice }
                              : {}),
                          }));
                        }}
                      >
                        {TRIAGE_TTS_PROVEEDORES.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      {envFor(TRIAGE_TTS_PROVEEDORES, triageVoz.ttsProveedor) && (
                        <span className={styles.fieldHint}>
                          Requiere {envFor(TRIAGE_TTS_PROVEEDORES, triageVoz.ttsProveedor)} en el
                          deploy del agente.
                        </span>
                      )}
                    </div>

                    {/* TTS voz */}
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Voz (femenina, español)</label>
                      <input
                        type="text"
                        className={styles.textInput}
                        value={triageVoz.ttsVoice}
                        onChange={(e) => setTriageVoz((c) => ({ ...c, ttsVoice: e.target.value }))}
                        placeholder={
                          TRIAGE_TTS_PROVEEDORES.find((p) => p.value === triageVoz.ttsProveedor)
                            ?.vozHint || ''
                        }
                      />
                      <span className={styles.fieldHint}>
                        {triageVoz.ttsProveedor === 'elevenlabs'
                          ? 'ElevenLabs (más natural en español latino) — pega el voice_id de una voz femenina de la voice library (elevenlabs.io). Vacío = voz por defecto.'
                          : triageVoz.ttsProveedor === 'xai'
                            ? 'xAI Grok — femeninas: Ara (cálida, recomendada) o Eve (natural). El acento latino se define con el idioma (es-MX).'
                            : triageVoz.ttsProveedor === 'deepgram'
                              ? 'Deepgram Aura-2 — femeninas en español: aura-2-selena-es, aura-2-carina-es, aura-2-diana-es.'
                              : 'Google — p. ej. es-US-Neural2-A (femenina).'}
                      </span>
                    </div>

                    {/* TTS respaldo */}
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Voz de respaldo (TTS)</label>
                      <select
                        className={styles.textInput}
                        value={triageVoz.ttsProveedorRespaldo}
                        onChange={(e) => {
                          const nuevo = e.target.value;
                          const hint =
                            TRIAGE_TTS_PROVEEDORES.find((p) => p.value === nuevo)?.vozHint || '';
                          setTriageVoz((c) => ({ ...c, ttsProveedorRespaldo: nuevo, ttsVoiceRespaldo: hint }));
                        }}
                      >
                        {opcionesRespaldo(TRIAGE_TTS_PROVEEDORES)
                          .filter((p) => p.value !== triageVoz.ttsProveedor)
                          .map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                      </select>
                      <span className={styles.fieldHint}>
                        Si la voz principal falla, el asistente sigue hablando con esta.
                      </span>
                    </div>
                    {triageVoz.ttsProveedorRespaldo !== 'ninguno' && (
                      <div className={styles.field}>
                        <label className={styles.fieldLabel}>Voz del respaldo</label>
                        <input
                          type="text"
                          className={styles.textInput}
                          value={triageVoz.ttsVoiceRespaldo}
                          onChange={(e) =>
                            setTriageVoz((c) => ({ ...c, ttsVoiceRespaldo: e.target.value }))
                          }
                          placeholder={
                            TRIAGE_TTS_PROVEEDORES.find((p) => p.value === triageVoz.ttsProveedorRespaldo)
                              ?.vozHint || ''
                          }
                        />
                      </div>
                    )}

                    {/* Idioma primario (solo xai/google; deepgram lo lleva en la voz) */}
                    {triageVoz.ttsProveedor !== 'deepgram' && (
                      <div className={styles.field}>
                        <label className={styles.fieldLabel}>Idioma primario</label>
                        <select
                          className={styles.textInput}
                          value={triageVoz.ttsIdioma}
                          onChange={(e) =>
                            setTriageVoz((c) => ({ ...c, ttsIdioma: e.target.value }))
                          }
                        >
                          {TRIAGE_TTS_IDIOMAS.map((l) => (
                            <option key={l} value={l}>
                              {l}
                            </option>
                          ))}
                        </select>
                        <span className={styles.fieldHint}>
                          Español como primer idioma de la voz.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Prompt del asistente de voz */}
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Prompt del asistente de voz</label>
                  <textarea
                    className={styles.promptTextarea}
                    value={triageVoz.promptVoz}
                    disabled={!triageVoz.triageVozHabilitado}
                    onChange={(e) =>
                      setTriageVoz((c) => ({ ...c, promptVoz: e.target.value }))
                    }
                    spellCheck={false}
                    placeholder={DEFAULT_PROMPT_VOZ}
                  />
                  <span className={styles.fieldHint}>
                    Define la personalidad y el comportamiento del asistente durante la llamada.
                    Debe conservar los marcadores <code>{'{datos_paciente}'}</code> y{' '}
                    <code>{'{prompt_clinico}'}</code>: el agente los reemplaza automáticamente por
                    los datos del paciente y por el prompt clínico del triage (que se edita en{' '}
                    <strong>Prompt de Triage</strong>). Si deja este campo vacío, el asistente usa
                    su plantilla interna por defecto.
                  </span>
                  <div>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      disabled={!triageVoz.triageVozHabilitado}
                      onClick={() =>
                        setTriageVoz((c) => ({ ...c, promptVoz: DEFAULT_PROMPT_VOZ }))
                      }
                    >
                      Restaurar prompt por defecto
                    </button>
                  </div>
                </div>

                <div className={styles.sectionActions} style={{ alignItems: 'center', gap: 10 }}>
                  <BadgeCambiosSinGuardar visible={hayCambiosTriageVoz} />
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={savingTriageVoz}
                    onClick={handleSaveTriageVoz}
                  >
                    <Save size={16} />
                    {savingTriageVoz ? 'Guardando…' : 'Guardar triage por voz'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ═══════════════ OPTIMIZACIÓN (VAD) ═══════════════ */}
          {activeTab === 'vad' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <Waves size={16} className={styles.titleIcon} />
                Optimización de transcripción (VAD)
              </h2>
              <div className={styles.sectionBody}>
                <p className={styles.sectionDescription}>
                  Detección de actividad de voz (VAD) para enviar al motor de transcripción solo
                  los tramos con voz. Reduce costo y latencia sin perder contenido clínico.
                </p>
                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>
                    Ante la duda, sesgar a permisivo (umbral más bajo, más pre-roll/hangover): es
                    preferible enviar un poco más de audio que perder palabras clínicas. Los cambios
                    aplican en la próxima llamada.
                  </span>
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Sensibilidad del VAD (0–1)</label>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    className={styles.textInput}
                    value={vad.activationThreshold}
                    onChange={(e) =>
                      setVad((c) => ({
                        ...c,
                        activationThreshold: toNumber(e.target.value, c.activationThreshold),
                      }))
                    }
                  />
                  <span className={styles.fieldHint}>
                    Más bajo = más permisivo (menos recorte, menos ahorro).
                  </span>
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Duración mínima de voz (ms)</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className={styles.textInput}
                    value={vad.minSpeechMs}
                    onChange={(e) =>
                      setVad((c) => ({ ...c, minSpeechMs: toNumber(e.target.value, c.minSpeechMs) }))
                    }
                  />
                  <span className={styles.fieldHint}>
                    Duración mínima para considerar voz. Evita disparos por golpes/ruidos cortos.
                  </span>
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Silencio mínimo — hangover (ms)</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className={styles.textInput}
                    value={vad.minSilenceMs}
                    onChange={(e) =>
                      setVad((c) => ({
                        ...c,
                        minSilenceMs: toNumber(e.target.value, c.minSilenceMs),
                      }))
                    }
                  />
                  <span className={styles.fieldHint}>
                    Silencio mínimo para cerrar la compuerta. Evita cortar entre palabras.
                  </span>
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Pre-roll — padding inicial (ms)</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className={styles.textInput}
                    value={vad.prefixPaddingMs}
                    onChange={(e) =>
                      setVad((c) => ({
                        ...c,
                        prefixPaddingMs: toNumber(e.target.value, c.prefixPaddingMs),
                      }))
                    }
                  />
                  <span className={styles.fieldHint}>
                    Audio incluido antes del inicio detectado. No cortar la primera sílaba.
                  </span>
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Confianza mínima final (0–1)</label>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    className={styles.textInput}
                    value={vad.finalMinConfidence}
                    onChange={(e) =>
                      setVad((c) => ({
                        ...c,
                        finalMinConfidence: toNumber(e.target.value, c.finalMinConfidence),
                      }))
                    }
                  />
                  <span className={styles.fieldHint}>
                    Umbral para descartar transcripciones finales de baja confianza (ruido
                    residual).
                  </span>
                </div>

                <div className={styles.sectionActions} style={{ alignItems: 'center', gap: 10 }}>
                  <BadgeCambiosSinGuardar visible={hayCambiosVad} />
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={savingVad}
                    onClick={handleSaveVad}
                  >
                    <Save size={16} />
                    {savingVad ? 'Guardando…' : 'Guardar optimización'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ═══════════════ HORARIO DE ENCENDIDO (WATCHDOG) ═══════════════ */}
          {activeTab === 'watchdog' && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <Clock size={16} className={styles.titleIcon} />
                Horario de encendido de los agentes de voz
              </h2>
              <div className={styles.sectionBody}>
                <p className={styles.sectionDescription}>
                  Controla cuándo se encienden los 3 agentes de voz que corren en un servidor
                  aparte: el que transcribe las teleconsultas, el asistente de configuración por
                  voz, y el triage por voz del paciente.
                </p>

                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>
                    Ese servidor se APAGA a propósito fuera de un horario, para ahorrar costo —
                    <strong> no es una falla</strong>. Por defecto puede estar apagado entre las
                    5:00 p.m. y las 6:00 a.m., y los fines de semana. Esta pantalla define a qué
                    hora y en qué días un proceso vigilante debe intentar ENCENDER de nuevo los 3
                    agentes. Si alguien reporta que "la transcripción no funciona" fuera del
                    horario configurado aquí, use el botón "Iniciar ahora" en vez de reportarlo
                    como una falla.
                  </span>
                </div>
                <div className={styles.infoBox}>
                  <Info size={14} />
                  <span>
                    El vigilante revisa esta configuración cada pocos minutos (no es instantáneo);
                    espere 2–5 minutos después de guardar o de tocar "Iniciar ahora" para ver el
                    efecto. Encender los agentes cuando el servidor sigue apagado no causa ningún
                    daño — simplemente lo van a intentar y no lo van a lograr hasta que el
                    servidor esté disponible de nuevo.
                  </span>
                </div>

                {/* Habilitar / deshabilitar horario automático */}
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>
                    <Power size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                    Horario automático
                  </label>
                  <div className={styles.toggleRow}>
                    <label className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={watchdog.habilitado}
                        onChange={(e) => setWatchdog((c) => ({ ...c, habilitado: e.target.checked }))}
                      />
                      {watchdog.habilitado
                        ? 'Habilitado — se enciende solo a la hora y días configurados abajo'
                        : 'Deshabilitado — solo se encenderá con el botón "Iniciar ahora"'}
                    </label>
                  </div>
                </div>

                <div className={styles.grid}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>
                      <Clock size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                      Hora de inicio (Panamá)
                    </label>
                    <input
                      type="time"
                      className={styles.textInput}
                      value={watchdog.horaInicio}
                      onChange={(e) => setWatchdog((c) => ({ ...c, horaInicio: e.target.value }))}
                    />
                    <span className={styles.fieldHint}>
                      Hora a la que el vigilante intenta encender los agentes cada día activo.
                    </span>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Margen de tolerancia (minutos)</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      className={styles.textInput}
                      value={watchdog.ventanaToleranciaMin}
                      onChange={(e) =>
                        setWatchdog((c) => ({
                          ...c,
                          ventanaToleranciaMin: toNumber(e.target.value, c.ventanaToleranciaMin),
                        }))
                      }
                    />
                    <span className={styles.fieldHint}>
                      El vigilante no corre exactamente a la hora en punto; este margen evita que
                      se "pase" el disparo. 10 minutos es un valor razonable.
                    </span>
                  </div>
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>
                    <Calendar size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                    Días activos
                  </label>
                  <div className={styles.toggleRow} style={{ flexWrap: 'wrap', gap: 8 }}>
                    {DIAS_SEMANA.map((d) => (
                      <label
                        key={d.value}
                        className={styles.toggle}
                        style={{
                          border: '1px solid var(--tc-line, #d8dee4)',
                          borderRadius: 8,
                          padding: '6px 10px',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={watchdog.diasActivos.includes(d.value)}
                          onChange={() => toggleDiaActivo(d.value)}
                        />
                        {d.label}
                      </label>
                    ))}
                  </div>
                  <span className={styles.fieldHint}>
                    Días de la semana en que aplica el horario. Se puede dejar marcados todos los
                    días — si el servidor sigue apagado ese día (ej. fin de semana), el intento
                    simplemente no logrará conectar y no pasa nada.
                  </span>
                </div>

                <div className={styles.sectionActions} style={{ alignItems: 'center', gap: 10 }}>
                  <BadgeCambiosSinGuardar visible={hayCambiosWatchdog} />
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={savingWatchdog}
                    onClick={handleSaveWatchdog}
                  >
                    <Save size={16} />
                    {savingWatchdog ? 'Guardando…' : 'Guardar horario'}
                  </button>
                </div>

                {/* ── Inicio manual ── */}
                <div className={styles.field} style={{ marginTop: 24 }}>
                  <label className={styles.fieldLabel}>Encender ahora</label>
                  <span className={styles.fieldHint} style={{ display: 'block', marginBottom: 10 }}>
                    Fuerza el encendido de los 3 agentes de inmediato, sin esperar el horario
                    programado. Útil si alguien necesita usar teleconsulta fuera de horario y el
                    servidor SÍ está disponible manualmente en este momento.
                  </span>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={iniciandoManual}
                    onClick={handleIniciarManual}
                  >
                    <PlayCircle size={16} />
                    {iniciandoManual ? 'Enviando solicitud…' : 'Iniciar ahora'}
                  </button>
                </div>

                {/* ── Estado ── */}
                <div className={styles.infoBox} style={{ marginTop: 20, flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                  <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Info size={14} /> Estado
                  </strong>
                  <span>Último encendido automático: {formatTs(watchdogStatus.ultimoReinicioAutomaticoEn)}</span>
                  <span>Último encendido manual: {formatTs(watchdogStatus.ultimoReinicioManualEn)}</span>
                  {watchdogStatus.reinicioManualSolicitadoEn &&
                    (!watchdogStatus.reinicioManualProcesadoEn ||
                      watchdogStatus.reinicioManualSolicitadoEn.toMillis() >
                        watchdogStatus.reinicioManualProcesadoEn.toMillis()) && (
                      <span>
                        <Loader2 size={12} className={styles.spinner} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                        Solicitud de inicio manual pendiente de procesar por el vigilante…
                      </span>
                    )}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
